import React, { useEffect, useMemo, useState, useCallback, useRef, memo } from 'react'
import Confetti from 'react-confetti'
import {
  Controller,
  useWatch,
  SubmitErrorHandler,
  FormProvider,
  SubmitHandler,
} from 'react-hook-form'
import { useHistory } from 'react-router'
import { useWindowSize } from 'react-use'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Grid, Box, Divider, Typography, Hidden, makeStyles, Theme } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { SHIPMENT_STATUS } from 'types'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { BooleanParam } from 'use-query-params'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { ControlledMotorCarrierUserSelect } from 'apps/fleet-planner/components/ControlledMotorCarrierUserSelect'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { bookingPermissionTestOptions } from 'apps/fleet-planner/hooks/useCarrierMembershipApi/schema/MotorCarrierUserForCurrentUserSchema'
import { useDriverApi } from 'apps/fleet-planner/hooks/useDriverApi'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { useTrackConversion } from 'apps/fleet-planner/hooks/useTrackConversion'
import { ITruck } from 'apps/fleet-planner/types'
import { IRateConfirmationParams } from 'apps/fleet-planner/types/mobileApp'
import { isCarrierAllowedToBookShipment } from 'apps/fleet-planner/utils/isCarrierAllowedToBookShipment'
import { lessThanHours } from 'apps/marketplace/utils/helpers/appointmentTime'
import { LeftArrowIcon } from 'transfix-icons'
import { Button, Spinner, Toast, useToastState } from 'transfix-ui'
import { useQueryParams } from 'utils/hooks'
import { BidModal } from '../../../marketplace/load-details/BidModal'
import { CarrierCompliance } from '../../CarrierCompliance'
import { DispatcherSelect } from '../../DispatcherSelect'
import { DriverOptions } from '../../DriverOptions'
import { Equipment } from '../../Equipment'
import { EquipmentDetailOptions } from '../../EquipmentDetailOptions'
import { LoadDetail } from '../../LoadDetail'
import { PaymentTerms } from '../../PaymentTerms'
import { RateConfirmationErrorState } from '../../RateConfirmationErrorState'
import { ShipmentTerms } from '../../ShipmentTerms'
import { TrackingMethod } from '../../TrackingMethod'
import { FIELD_HIERARCHY, TEST_IDS } from './constants'
import { useRateConfirmationForm } from './hooks/useRateConfirmationForm'
import { TRateConfirmationFormSchema } from './schema/RateConfirmationFormSchema'
import { TInputRefType } from './types'
import {
  getDefaultTrackingMethod,
  getErrorMessage,
  getSignRateConfirmationRequestBody,
} from './utils'

export const useRateConfirmationFormStyles = makeStyles<Theme, IRateConfirmationParams>(theme => ({
  root: {
    padding: theme.spacing(2),
    paddingTop: ({ mobileApp }) => theme.spacing(mobileApp ? 3 : 12),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(12),
      paddingRight: theme.spacing(5),
    },
  },
  divider: {
    marginTop: theme.spacing(4),
  },
  form: {
    overflow: 'hidden',
  },
  navBar: {
    top: theme.spacing(9),
    position: 'sticky',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    boxShadow: theme.shadows[1],
    display: 'flex',
    alignItems: 'center',
    zIndex: 3,
    backgroundColor: brandColors.white,
    [theme.breakpoints.up('md')]: {
      top: 0,
    },
  },
  leftArrow: {
    height: 18,
    width: 18,
    marginRight: theme.spacing(4),
    color: brandColors.skyBlue6,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
}))

export interface IRateConfirmationFormProps {}

export const RateConfirmationForm = memo<IRateConfirmationFormProps>(() => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const [{ mobileApp }] = useQueryParams<IRateConfirmationParams>({
    mobileApp: BooleanParam,
  })
  const classes = useRateConfirmationFormStyles({ mobileApp })
  const { height, width } = useWindowSize()
  const { show: showChatWidget } = useChatWidgetContext()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: confettisEnabled } = getFlag('confettis')

  const [confettisActivated, setConfettisActivated] = useState(false)
  const [isBidModalOpen, setIsBidModalOpen] = useState(false)
  const [isFormLoading, setLoading] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [rateErrorState, setRateErrorState] = useState<boolean>(false)

  const carrierSignatureRef = useRef<HTMLInputElement>(null)
  const dispatcherRef = useRef<HTMLInputElement>(null)
  const driverRef = useRef<HTMLInputElement>(null)
  const motorCarrierUserRef = useRef<HTMLInputElement>(null)
  const trackingMethodRef = useRef<HTMLDivElement>(null)
  const vinRef = useRef<HTMLInputElement>(null)

  /**
   * Stores input refs so we can trigger `onFocus` prop callback
   * with <Controller /> when there's an error
   */
  const inputRefs: TInputRefType = useMemo(
    () => ({
      carrierSignature: carrierSignatureRef,
      dispatcherId: dispatcherRef,
      driverId: driverRef,
      motorCarrierUser: motorCarrierUserRef,
      trackingMethod: trackingMethodRef,
      vin: vinRef,
    }),
    []
  )

  const {
    carrierApplication,
    carrierCompliance,
    errors: apiErrors,
    getMarketplaceCarrierApplication,
    getMarketplaceShipmentCarrierCompliance,
    isLoadingCarrierApplication,
    isLoadingShipmentCarrierCompliance,
    rateConSigned,
    resetCarrierApplication,
    resetMarketplaceShipmentCarrierCompliance,
    setActiveShipmentDetailsId,
    shipmentDetails: shipment,
    signMarketplaceLoadRateCon,
    updateError,
  } = useMarketplaceContext()
  const { userData } = useUserContext()
  const { user } = userData || {}
  const { id: currentUserId } = user || {}
  const { motorCarrierUsers } = useMembershipContext()
  const { bids, id: shipmentId, info, status, stops } = shipment || {}
  const { key: stateKey } = status || {}
  const {
    carrierFleetPlannerMotorCarrierId,
    dispatcherParentUserId: shipmentDispatcherId,
    trailerNumber,
    truckNumber,
  } = info || {}

  const isCounterBid = bids?.some(b => b.status === 'countered')
  const isOffer = useMemo(
    () => [SHIPMENT_STATUS.offered, SHIPMENT_STATUS.waterfallOffered].includes(stateKey),
    [stateKey]
  )

  const redirectToShipmentHandler = useCallback(() => {
    if (mobileApp) {
      ReactNativeWebView.postMessage('RATE_SUBMITTED')
    }
    const mobileAppParam = mobileApp ? '&mobileApp=1' : ''
    return history.push(
      `/external_load_sync/${shipmentId}?origin=rateConfirmation${mobileAppParam}`
    )
  }, [history, shipmentId, mobileApp])

  const { clearTrackConversionData, sourceChannel, sourceSegment } = useTrackConversion()

  const defaultMotorCarrierUser = useMemo(() => {
    // Default to the offered carrier if exists
    if (carrierFleetPlannerMotorCarrierId) {
      return motorCarrierUsers.find(
        mcu => mcu.motorCarrier.id === String(carrierFleetPlannerMotorCarrierId)
      )
    }

    if (motorCarrierUsers.length === 1) {
      return motorCarrierUsers[0]
    }

    return undefined
  }, [carrierFleetPlannerMotorCarrierId, motorCarrierUsers])

  const defaultDispatcherId = useMemo(() => {
    if (shipmentDispatcherId) {
      return String(shipmentDispatcherId)
    }

    return currentUserId
  }, [currentUserId, shipmentDispatcherId])

  const defaultTrackingMethod = useMemo(() => getDefaultTrackingMethod(carrierApplication), [
    carrierApplication,
  ])

  const methods = useRateConfirmationForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      carrierSignature: undefined,
      dispatcherId: defaultDispatcherId,
      /**
       * @todo Prefill driverId with coverage info (if exists)
       */
      driverId: undefined,
      driverRequired: false,
      motorCarrierUser: defaultMotorCarrierUser,
      trackingMethod: defaultTrackingMethod,
      trailerNumber,
      truckNumber,
    },
  })

  const { control, errors: formErrors, getValues, handleSubmit, setValue } = methods

  const pickupTime = useMemo(
    () => (stops && stops.length > 0 ? stops[0]?.appointment?.startAt : ''),
    [stops]
  )

  const picksUpSoon = pickupTime ? lessThanHours(pickupTime, 36) : false

  const {
    automotiveInsurance: { policyType = '' } = {},
    isEldApproved: isProject44Enabled = false,
    keepTruckinEnabled: isKeepTruckinEnabled = false,
  } = carrierApplication || {}

  const isScheduledAuto = policyType === 'Scheduled Auto'
  const vinRequired = isScheduledAuto && picksUpSoon

  const motorCarrierUser = useWatch<TRateConfirmationFormSchema['motorCarrierUser']>({
    control,
    name: 'motorCarrierUser',
  })

  const { motorCarrier } = motorCarrierUser ?? {}
  const { dotNumber: motorCarrierDotNumber, id: motorCarrierId, mcNumber: motorCarrierMcNumber } =
    motorCarrier ?? {}

  const trackingMethod =
    useWatch<TRateConfirmationFormSchema['trackingMethod']>({
      control,
      name: 'trackingMethod',
    }) ?? defaultTrackingMethod

  const { getDrivers, drivers = [] } = useDriverApi()

  // Clear track conversion data on back button press.
  useEffect(() => {
    return () => {
      if (!mobileApp && history.action === 'POP') {
        clearTrackConversionData()
      }
    }
  }, [clearTrackConversionData, history, mobileApp])

  // On rate con submission success, redirect to /external_load_sync/:shipmentId
  useEffect(() => {
    if (rateConSigned) {
      setConfettisActivated(true)
      if (!confettisEnabled) {
        redirectToShipmentHandler()
      } else {
        setTimeout(() => setConfettisActivated(false), 3000)
      }
    }
    // Cleanup rate con state
    return () => resetMarketplaceShipmentCarrierCompliance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateConSigned, shipmentId, confettisEnabled, redirectToShipmentHandler])

  /**
   * Fetch the `CarrierApplication`, compliance info, and drivers for the selected motor carrier.
   */
  useEffect(() => {
    resetCarrierApplication()
    resetMarketplaceShipmentCarrierCompliance()

    if (motorCarrierDotNumber && motorCarrierId) {
      getMarketplaceCarrierApplication(motorCarrierDotNumber, motorCarrierMcNumber ?? null)
      getMarketplaceShipmentCarrierCompliance(
        shipmentId,
        motorCarrierDotNumber,
        motorCarrierMcNumber ?? null
      )
      getDrivers({ filters: { motorCarrierId } })
    }
  }, [
    getMarketplaceCarrierApplication,
    shipmentId,
    getMarketplaceShipmentCarrierCompliance,
    getDrivers,
    motorCarrierDotNumber,
    motorCarrierId,
    motorCarrierMcNumber,
    resetCarrierApplication,
    resetMarketplaceShipmentCarrierCompliance,
  ])

  /**
   * Update the tracking method to the newly calculated default (which happens when the selected carrier changes)
   */
  useEffect(() => {
    if (defaultTrackingMethod) {
      setValue('trackingMethod', defaultTrackingMethod)
    }
  }, [defaultTrackingMethod, setValue])

  /**
   * This method refresh user information when the user comes back to this page to check that
   * the user successfully connected their account with KeepTruckin
   */
  const onVisibilityHandler = useCallback(() => {
    if (document.visibilityState === 'visible' && motorCarrierDotNumber) {
      getMarketplaceCarrierApplication(motorCarrierDotNumber, motorCarrierMcNumber ?? null)
    }
  }, [getMarketplaceCarrierApplication, motorCarrierDotNumber, motorCarrierMcNumber])

  /**
   * The purpose useEffect is to register the visibility listener,
   * we need to check if the user ended successfully its process to connect with KeepTruckin
   * when she/he came back to this page
   */
  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityHandler)

    return () => document.removeEventListener('visibilitychange', onVisibilityHandler)
  }, [onVisibilityHandler])

  const submitHandler: SubmitHandler<TRateConfirmationFormSchema> = useCallback(
    async data => {
      setLoading(true)

      const requestBody = getSignRateConfirmationRequestBody(data, sourceChannel, sourceSegment)

      // Record form submission success
      trackEvent('Rate Con', 'SUBMIT_RATE_CON_FORM', {
        shipmentId,
        sourceChannel,
        sourceSegment,
        formValues: data,
      })

      if (!sourceChannel || !sourceSegment) {
        trackEvent(
          'Missing Attribution Source',
          'SUBMITTED_RATE_CON_WITH_MISSING_ATTRIBUTION_SOURCE'
        )
      }

      if (isCounterBid) {
        trackEvent('Rate Con', 'SUBMIT_RATE_CON_FOR_COUNTERBID')
      }

      await signMarketplaceLoadRateCon(requestBody, `${shipmentId}`, setRateErrorState)

      if (updateError && rateConSigned === false) {
        trackEvent('Rate Con', 'FAILED_SUBMIT_RATE_CON_FORM', { shipmentId, updateError })
        const errorMessage = (
          <span>
            There was an error completing your request. If this issue persists, please{' '}
            <Button
              className='btn-text-link-b'
              style={{
                color: 'white',
              }}
              onClick={showChatWidget}>
              contact us
            </Button>
          </span>
        )
        openToast({
          toastType: 'alert',
          toastMessage: errorMessage,
        })
      }

      // Loading is toggled when async action is complete,
      // whether it's success or failure
      setLoading(false)

      clearTrackConversionData()
    },
    [
      clearTrackConversionData,
      isCounterBid,
      openToast,
      rateConSigned,
      shipmentId,
      showChatWidget,
      signMarketplaceLoadRateCon,
      sourceChannel,
      sourceSegment,
      trackEvent,
      updateError,
    ]
  )

  const submitErrorHandler: SubmitErrorHandler<TRateConfirmationFormSchema> = useCallback(
    errors => {
      setShowError(true)

      const { motorCarrierUser: motorCarrierUserErrors } = errors ?? {}
      const { type: motorCarrierUserErrorType } = motorCarrierUserErrors ?? {}

      const hasPermissionToBook = motorCarrierUserErrorType !== bookingPermissionTestOptions.name

      const toastMessage = !hasPermissionToBook
        ? 'You do not have permission to book this load, please contact customer support.'
        : 'Please check the form for errors.'

      openToast({
        toastType: 'alert',
        toastMessage,
      })

      if (!hasPermissionToBook) {
        trackEvent('Rate Con', 'ERROR_USER_DOES_NOT_HAVE_PERMISSION', {
          shipmentId,
          formValues: getValues(),
          motorCarrierUser,
        })
      }

      trackEvent('Rate Con', 'FAILED_SUBMIT_RATE_CON_FORM', {
        shipmentId,
        formValues: getValues(),
        formErrors: errors,
      })
    },
    [getValues, motorCarrierUser, openToast, shipmentId, trackEvent]
  )

  const openBidModalHandler = () => {
    if (mobileApp) {
      ReactNativeWebView.postMessage('NOT_COMPLIANT')
      return
    }
    setActiveShipmentDetailsId(shipmentId)
    setIsBidModalOpen(true)
  }

  const closeBidModalHandler = () => {
    setActiveShipmentDetailsId(undefined)
    setIsBidModalOpen(false)
  }

  const bidSuccessHandler = () => {
    history.push(`/book/${shipmentId}`)
  }

  const bidErrorHandler = () =>
    openToast({
      toastType: 'alert',
      toastMessage: 'Error Submitting Bid',
    })

  /**
   * When this fails, trigger submitErrorHandler, which logs issues to
   * Sentry and Mixpanel
   */
  const clickSubmitHandler = handleSubmit(submitHandler, submitErrorHandler)

  const defaultDriverHandler = useCallback((driverId: number) => setValue('driverId', driverId), [
    setValue,
  ])

  const clickCancelHandler = () => {
    trackEvent('Rate Con', 'CANCELED_RATE_CONFIRMATION', { shipmentId })
    if (history?.length > 1) {
      history.goBack()
    } else {
      history.replace('/book')
    }
  }

  const setDriverIsRequiredHandler = useCallback(
    (required: boolean) => setValue('driverRequired', required),
    [setValue]
  )

  const focusOnErrorElement = useCallback(() => {
    if (!showError) return

    setShowError(false)
    const field = FIELD_HIERARCHY.find(key => formErrors[key])

    if (!field) return

    const selectedRef = inputRefs[field]

    if (!selectedRef || !selectedRef?.current) return

    if (
      selectedRef?.current?.localName === 'div' &&
      typeof selectedRef?.current?.scrollIntoView === 'function'
    ) {
      // handles focus/scroll for div elements
      selectedRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
    } else if (typeof selectedRef?.current?.focus === 'function') {
      // handles focus for input elements
      selectedRef.current.focus()
    }
  }, [formErrors, inputRefs, showError])

  /**
   * check if an error was triggered and focus the element that is not properly filled
   */
  useEffect(() => {
    focusOnErrorElement()
  }, [focusOnErrorElement])

  const onTruckChangeHandler = useCallback(
    (selectedTruck?: Partial<ITruck>) => {
      const { vin } = selectedTruck || {}
      if (vin && vinRequired) {
        setValue('vin', vin)
      }
    },
    [setValue, vinRequired]
  )

  const onTrailerChangeHandler = useCallback(
    (trailerNumber?: string) => setValue('trailerNumber', trailerNumber),
    [setValue]
  )

  // Instructs the mobile app to navigate back from rate confirmation in case of error
  useEffect(() => {
    if (rateErrorState && mobileApp) {
      ReactNativeWebView.postMessage('ERROR')
    }
  }, [rateErrorState, mobileApp])

  const isAllowedToBook = useMemo(
    () => isCarrierAllowedToBookShipment(carrierCompliance, shipment),
    [carrierCompliance, shipment]
  )

  /**
   * @description Display compliance warning once data for the selected carrier is loaded and the carrier is
   * non-compliant for the load or non-compliant generally.
   */
  const showCarrierComplianceWarning = useMemo(
    () => Boolean(carrierApplication) && Boolean(carrierCompliance) && !isAllowedToBook,
    [carrierApplication, carrierCompliance, isAllowedToBook]
  )

  // returns an error state, rather than infinite loading a spinner in the component below
  if (rateErrorState) {
    return <RateConfirmationErrorState shipmentId={shipmentId} />
  }

  const showListRateOverride = isCounterBid
    ? motorCarrierUser?.motorCarrier.dotNumber !==
      Number(bids?.find(b => b.status === 'countered')?.carrierDotNumber)
    : false

  return (
    <Box>
      <Hidden only={['xs', 'sm', 'md']}>
        <Box className={classes.navBar}>
          <Box className={classes.iconContainer} onClick={clickCancelHandler}>
            <LeftArrowIcon className={classes.leftArrow} />
          </Box>
          <Typography variant='h3'>Confirm and book</Typography>
        </Box>
      </Hidden>
      <FormProvider {...methods}>
        <form className={classes.form}>
          <div className={classes.root}>
            <Grid container spacing={10}>
              <Grid item md={12} xs={12}>
                <Grid container spacing={10}>
                  <Grid item md={12} lg={6} xs={12}>
                    <Box>
                      <Typography variant='h2'>Confirm and book</Typography>
                      {apiErrors && (
                        <Box mt={2} mb={2}>
                          <InlineMessage message={apiErrors} type={INLINE_MESSAGE_VARIANTS.ALERT} />
                        </Box>
                      )}
                      <Box>
                        Please fill out the information below and accept this load at the bottom of
                        this form. You’ll receive an email with a signed rate confirmation
                        afterwards.
                      </Box>
                    </Box>
                    <Hidden only={['lg', 'xl']}>
                      <Box mt={4}>
                        <LoadDetail
                          shipment={shipment}
                          showListRateOverride={showListRateOverride}
                        />
                      </Box>
                    </Hidden>
                    <Box mt={4}>
                      <Typography variant='h3'>Your company</Typography>
                      <Box mt={2}>
                        <ControlledMotorCarrierUserSelect
                          control={control}
                          name='motorCarrierUser'
                          defaultValue={defaultMotorCarrierUser}
                          SelectProps={{
                            dataTest: TEST_IDS.motorCarrierUserSelect,
                            disabled: isOffer,
                            inputRef: motorCarrierUserRef,
                            large: true,
                            label: 'Select a carrier',
                            placeholder: 'Select one',
                            required: true,
                            error: Boolean(getErrorMessage(formErrors, 'motorCarrierUser')),
                            validationMessage: getErrorMessage(formErrors, 'motorCarrierUser'),
                          }}
                        />
                      </Box>

                      <Box my={4}>
                        {(isLoadingCarrierApplication || isLoadingShipmentCarrierCompliance) && (
                          <Spinner position='relative' />
                        )}

                        {showCarrierComplianceWarning && (
                          <CarrierCompliance onClickBid={openBidModalHandler} />
                        )}
                      </Box>

                      <Divider className={classes.divider} />
                    </Box>

                    <Box mt={4}>
                      <Typography variant='h3'>Assign to</Typography>
                      <Box mt={2}>
                        <DispatcherSelect
                          inputRef={dispatcherRef}
                          control={control}
                          motorCarrierId={motorCarrierId ? Number(motorCarrierId) : undefined}
                          formErrors={formErrors}
                          SelectProps={{
                            dataTest: TEST_IDS.dispatcherSelect,
                          }}
                        />
                      </Box>
                      <Box mt={2}>
                        <DriverOptions
                          inputRef={driverRef}
                          control={control}
                          drivers={drivers}
                          formErrors={formErrors}
                          setDefaultDriver={defaultDriverHandler}
                          setDriverIsRequired={setDriverIsRequiredHandler}
                        />
                      </Box>
                      <Divider className={classes.divider} />
                    </Box>
                    <Box mt={4}>
                      <Box mt={4}>
                        <Controller
                          control={control}
                          name='trackingMethod'
                          render={({ onChange = () => {} }) => {
                            const changeHandler = (t: TRACKING_METHOD_TYPE) => {
                              // Mixpanel events.
                              if (t === TRACKING_METHOD_TYPE.mobile) {
                                trackEvent('ELD', 'SELECTED_MOBILE_APP_TRACKING_METHOD_ON_RATE_CON')
                              } else if (t === TRACKING_METHOD_TYPE.keepTruckin) {
                                trackEvent('ELD', 'SELECTED_ELD_TRACKING_METHOD_ON_RATE_CON')
                                trackEvent(
                                  'ELD',
                                  'SELECTED_KEEP_TRUCKIN_TRACKING_METHOD_ON_RATE_CON'
                                )
                              } else if (t === TRACKING_METHOD_TYPE.project44) {
                                trackEvent('ELD', 'SELECTED_ELD_TRACKING_METHOD_ON_RATE_CON')
                                trackEvent('ELD', 'SELECTED_P44_TRACKING_METHOD_ON_RATE_CON')
                              }
                              onChange(t)
                            }
                            return (
                              <TrackingMethod
                                inputRef={trackingMethodRef}
                                isProject44Connected={isProject44Enabled}
                                isKeepTruckinConnected={isKeepTruckinEnabled}
                                defaultValue={defaultTrackingMethod}
                                carrierId={motorCarrierId ? Number(motorCarrierId) : undefined}
                                onChange={changeHandler}
                                formErrors={formErrors}
                              />
                            )
                          }}
                        />
                      </Box>
                      <Box mt={4}>
                        <Typography color='textPrimary' variant='subtitle1'>
                          Your equipment details
                        </Typography>
                        <Box>
                          If you don’t know this information yet, we will contact you before the
                          pickup to confirm it.
                        </Box>
                        <EquipmentDetailOptions
                          vinInputRef={vinRef}
                          selectedTrackingMethod={trackingMethod!}
                          control={control}
                          formErrors={formErrors}
                          carrierApplication={carrierApplication}
                          selectedMotorCarrierId={
                            motorCarrierId ? Number(motorCarrierId) : undefined
                          }
                          vinRequired={vinRequired}
                          onTruckChange={onTruckChangeHandler}
                          onTrailerChange={onTrailerChangeHandler}
                        />
                      </Box>
                      <Box mt={4}>
                        <Typography color='textPrimary' variant='subtitle1'>
                          Carrier requirements
                        </Typography>
                        <Box>
                          Please make sure you’re aware of the equipment and instructions below.
                        </Box>
                        <Equipment shipment={shipment} />
                      </Box>
                      <Divider className={classes.divider} />
                    </Box>
                    <Box mt={4}>
                      <Typography variant='h3'>Payment terms</Typography>
                      <PaymentTerms />
                      <Divider className={classes.divider} />
                    </Box>
                    <Box mt={4}>
                      <Typography variant='h3'>Shipment terms</Typography>
                      <Box mt={2} p={3} bgcolor='#F3F5F7'>
                        <ShipmentTerms />
                      </Box>
                    </Box>
                    <Box mt={4}>
                      <ControlledTextField
                        control={control}
                        name='carrierSignature'
                        TextInputProps={{
                          large: true,
                          placeholder: 'Enter your full name',
                          label: 'Your Full Name',
                          required: true,
                          autoComplete: 'off',
                          clearable: false,
                          error: Boolean(getErrorMessage(formErrors, 'carrierSignature')),
                          validationMessage: getErrorMessage(formErrors, 'carrierSignature'),
                          inputRef: carrierSignatureRef,
                          dataTest: TEST_IDS.carrierSignatureInput,
                        }}
                      />
                      <Box component='legend' className='label'>
                        This serves as a Carrier Representative signature.
                      </Box>
                    </Box>
                    <Hidden only={['xs', 'sm', 'md']}>
                      <Box mt={3} mb={18}>
                        <Button
                          data-test={TEST_IDS.submitButton}
                          data-intercom='fms-sign-rate-con-button'
                          color='primary'
                          size='large'
                          disabled={!isAllowedToBook || rateConSigned || isFormLoading}
                          onClick={clickSubmitHandler}>
                          Book Load
                        </Button>
                      </Box>
                    </Hidden>
                    <Hidden only={['lg', 'xl']}>
                      <Box mt={3} mb={4}>
                        <Button
                          data-test={TEST_IDS.submitButton}
                          data-intercom='fms-sign-rate-con-button'
                          color='primary'
                          fullWidth
                          disabled={!isAllowedToBook || rateConSigned || isFormLoading}
                          onClick={clickSubmitHandler}>
                          Book Load
                        </Button>
                      </Box>
                    </Hidden>
                  </Grid>
                  <Hidden only={['xs', 'sm', 'md']}>
                    <Grid item md={12} lg={6}>
                      <LoadDetail shipment={shipment} showListRateOverride={showListRateOverride} />
                    </Grid>
                  </Hidden>
                </Grid>
              </Grid>
            </Grid>

            {rateConSigned && confettisEnabled && (
              <Confetti
                tweenDuration={3000}
                width={width}
                height={height}
                colors={['#0072B5', '#0D0E10', '#0072B5', '#394657', '#025187', '#BEE7FD']}
                recycle={confettisActivated}
                onConfettiComplete={redirectToShipmentHandler}
              />
            )}
          </div>
        </form>
      </FormProvider>
      {isBidModalOpen && (
        <BidModal
          onClose={closeBidModalHandler}
          isOpen
          onSuccess={bidSuccessHandler}
          onError={bidErrorHandler}
        />
      )}
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </Box>
  )
})

export default RateConfirmationForm
